import { createAppRequest } from '../request';
import { BizAppConfig } from 'config/application.manifest';
import type { ApiResponse, InvestorPersonaItem, PageResult, PitchSimulationItem } from './startup.api';

export type { InvestorPersonaItem, PitchSimulationItem };

const appRequest = createAppRequest();

const getStreamApiUrl = (path: string): string => {
  const baseUrl = (BizAppConfig.appApiUrl || '').replace(/\/+$/, '');
  return `${baseUrl}${path}`;
};

const getStreamHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  const tenantUuid = localStorage.getItem('currentTenantUuid');
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
  return headers;
};

export interface PitchMessageItem {
  id?: number;
  uuid: string;
  tenantUuid?: string;
  pitchUuid: string;
  role: 'INVESTOR' | 'USER' | string;
  content: string;
  sequence: number;
  createdAt?: string;
}

export interface PitchReviewItem {
  id?: number;
  uuid: string;
  tenantUuid?: string;
  projectUuid?: string;
  pitchUuid: string;
  summary?: string;
  strengthsJson?: unknown[] | null;
  weaknessesJson?: unknown[] | null;
  riskQuestionsJson?: unknown[] | null;
  missingMaterialsJson?: unknown[] | null;
  bpRecommendationsJson?: unknown[] | null;
  modelInfo?: string;
  promptVersion?: string;
  createdAt?: string;
}

export interface PitchDetailItem extends PitchSimulationItem {
  persona?: InvestorPersonaItem;
  businessPlan?: {
    uuid: string;
    name: string;
    versionNo: number;
    fileName: string;
    isCurrent: boolean;
  };
  project?: {
    uuid: string;
    name: string;
  };
  messages?: PitchMessageItem[];
  review?: PitchReviewItem;
}

export interface CreatePitchPayload {
  projectUuid: string;
  businessPlanUuid: string;
  personaUuid: string;
}

export interface PitchQuery {
  projectUuid: string;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * 获取平台级投资人画像列表（财务型、技术型、产业型、挑战型）
 */
export async function getInvestorPersonas(): Promise<ApiResponse<InvestorPersonaItem[]>> {
  return appRequest('/bff/pitches/personas', {
    method: 'GET',
  });
}

/**
 * 分页查询某创业项目的模拟路演会话
 */
export async function getPitchSimulations(
  params: PitchQuery,
): Promise<ApiResponse<PageResult<PitchDetailItem>>> {
  return appRequest('/bff/pitches', {
    method: 'GET',
    params,
  });
}

/**
 * 获取模拟路演详情与对话历史
 */
export async function getPitchSimulationDetail(
  uuid: string,
): Promise<ApiResponse<PitchDetailItem>> {
  return appRequest(`/bff/pitches/${uuid}`, {
    method: 'GET',
  });
}

/**
 * 创建/发起模拟路演
 */
export async function createPitchSimulation(
  data: CreatePitchPayload,
): Promise<ApiResponse<PitchDetailItem>> {
  return appRequest('/bff/pitches', {
    method: 'POST',
    data,
  });
}

/**
 * 取消进行中的模拟路演
 */
export async function cancelPitchSimulation(
  uuid: string,
): Promise<ApiResponse<PitchDetailItem>> {
  return appRequest(`/bff/pitches/${uuid}/cancel`, {
    method: 'POST',
  });
}

/**
 * 获取模拟路演复盘报告
 */
export async function getPitchReview(
  uuid: string,
): Promise<ApiResponse<PitchReviewItem>> {
  return appRequest(`/bff/pitches/${uuid}/review`, {
    method: 'GET',
  });
}

/**
 * 生成首个投资人问题（保证幂等）
 */
export async function generateInitialQuestion(
  uuid: string,
): Promise<ApiResponse<PitchMessageItem>> {
  return appRequest(`/bff/pitches/${uuid}/initial-question`, {
    method: 'POST',
  });
}

/**
 * 结束路演并生成复盘
 */
export async function completePitchSimulation(
  uuid: string,
): Promise<ApiResponse<PitchReviewItem>> {
  return appRequest(`/bff/pitches/${uuid}/complete`, {
    method: 'POST',
  });
}

export interface StreamMessageCallbacks {
  onDelta?: (delta: string) => void;
  onDone?: (message: PitchMessageItem) => void;
  onError?: (error: Error) => void;
}

/**
 * 解析并处理 SSE 数据流
 */
async function processSseResponse(
  response: Response,
  callbacks?: StreamMessageCallbacks,
  startTime?: number,
): Promise<void> {
  const reqStart = startTime || Date.now();
  let firstDeltaTime = 0;
  let chunkCount = 0;
  let receivedDone = false;
  let reportedError = false;

  const reportError = (error: Error): void => {
    if (reportedError) return;
    reportedError = true;
    callbacks?.onError?.(error);
  };

  if (!response.ok) {
    let errorMsg = `请求失败: ${response.statusText}`;
    try {
      const errJson = await response.json();
      errorMsg = errJson.msg || errJson.message || errorMsg;
    } catch {
      // ignore
    }
    const err = new Error(errorMsg);
    reportError(err);
    throw err;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('text/event-stream')) {
    const err = new Error(`响应不是 SSE 流（Content-Type: ${contentType || '未知'}）`);
    reportError(err);
    throw err;
  }

  if (!response.body) {
    const err = new Error('响应未包含流数据');
    reportError(err);
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  let currentEvent = '';
  let currentData = '';

  const dispatchEvent = (): void => {
    if (!currentEvent) {
      currentData = '';
      return;
    }
    if (!currentData) {
      currentEvent = '';
      return;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(currentData);
    } catch {
      const err = new Error('流式响应事件格式错误');
      reportError(err);
      throw err;
    }

    if (currentEvent === 'delta' && parsed.text) {
      if (!firstDeltaTime) {
        firstDeltaTime = Date.now();
        console.log(`[SSE前端监控] 收到首字 delta，耗时: ${firstDeltaTime - reqStart}ms`);
      }
      chunkCount++;
      callbacks?.onDelta?.(parsed.text);
    } else if (currentEvent === 'done') {
      receivedDone = true;
      console.log(
        `[SSE前端监控] 流式结束，总耗时: ${Date.now() - reqStart}ms，共 ${chunkCount} 个分片`,
      );
      callbacks?.onDone?.(parsed);
    } else if (currentEvent === 'error') {
      const err = new Error(parsed.message || '流式输出异常');
      reportError(err);
      throw err;
    }
    currentEvent = '';
    currentData = '';
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith(':')) continue;
        if (line.startsWith('event:')) {
          currentEvent = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          currentData += line.slice('data:'.length).trim();
        } else if (line === '') {
          dispatchEvent();
        }
      }
      if (done) break;
    }

    if (buffer) {
      if (buffer.startsWith('event:')) currentEvent = buffer.slice('event:'.length).trim();
      else if (buffer.startsWith('data:')) currentData += buffer.slice('data:'.length).trim();
    }
    dispatchEvent();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    reportError(err);
    throw err;
  } finally {
    reader.releaseLock();
  }

  if (!receivedDone) {
    const err = new Error('流式响应未正常结束');
    reportError(err);
    throw err;
  }
}

/**
 * 发送用户回答并流式接收投资人下一提问 (SSE)
 */
export async function sendPitchMessageStream(
  uuid: string,
  content: string,
  callbacks?: StreamMessageCallbacks,
): Promise<void> {
  const startTime = Date.now();
  const response = await fetch(getStreamApiUrl(`/bff/pitches/${uuid}/messages`), {
    method: 'POST',
    headers: getStreamHeaders(),
    body: JSON.stringify({ content }),
  });

  await processSseResponse(response, callbacks, startTime);
}

/**
 * 重试最后一个投资人问题 (SSE)
 */
export async function retryPitchQuestionStream(
  uuid: string,
  callbacks?: StreamMessageCallbacks,
): Promise<void> {
  const response = await fetch(getStreamApiUrl(`/bff/pitches/${uuid}/retry-question`), {
    method: 'POST',
    headers: getStreamHeaders(),
  });

  await processSseResponse(response, callbacks);
}

/**
 * 流式生成路演首个投资人问题 (SSE)
 */
export async function generatePitchInitialQuestionStream(
  uuid: string,
  callbacks?: StreamMessageCallbacks,
): Promise<void> {
  const startTime = Date.now();
  const response = await fetch(getStreamApiUrl(`/bff/pitches/${uuid}/initial-question-stream`), {
    method: 'POST',
    headers: getStreamHeaders(),
  });

  await processSseResponse(response, callbacks, startTime);
}

